import { noteCardClass } from '@/components/CalloutNote'
import { NoteMarkdown } from '@/components/NoteMarkdown'
import { formatServiceStatus } from '@/help/serviceStatus'
import { formatRelativeDateTime } from '@/lib/relativeDateTime'
import { getServiceStatusMeta } from '@/lib/serviceStatusMeta'
import type { ServiceStatus } from '@/lib/schemas/db'

function formatDurationBetween(fromIso: string, toIso: string): string {
  const from = new Date(fromIso).getTime()
  const to = new Date(toIso).getTime()
  const diffMs = Math.max(0, to - from)

  const totalMinutes = Math.floor(diffMs / 60000)
  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
  const minutes = totalMinutes % 60

  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)

  return parts.length > 0 ? parts.join(' ') : '<1m'
}

export interface InvoiceStatusChangeItem {
  id: string
  createdAt: string
  previousStatus: ServiceStatus
  newStatus: ServiceStatus
  authorName: string
  reason?: string
}

export function InvoiceStatusChangesGroup({
  statusChanges,
  checked,
  onCheckedChange,
}: {
  statusChanges: InvoiceStatusChangeItem[]
  checked?: boolean
  onCheckedChange?: (next: boolean) => void
}) {
  if (statusChanges.length === 0) return null

  const sorted = [...statusChanges].sort((a, b) => a.createdAt.localeCompare(b.createdAt))

  return (
    <div
      className={`w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-left ${onCheckedChange ? 'cursor-pointer' : ''}`}
      onClick={
        onCheckedChange
          ? () => onCheckedChange(!Boolean(checked))
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-xs font-semibold text-slate-800">Status changes (grouped)</p>
          <p className="text-[11px] text-slate-500">
            {onCheckedChange
              ? 'Single checklist item for all status changes. Includes elapsed time between changes.'
              : 'Grouped status timeline with elapsed time between changes.'}
          </p>
        </div>
        {onCheckedChange ? (
          <input
            type="checkbox"
            className="mt-1 h-6 w-6 rounded border-2 border-slate-500 text-slate-900 accent-slate-900"
            checked={Boolean(checked)}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => onCheckedChange(event.target.checked)}
            aria-label="Mark all status changes as handled"
          />
        ) : null}
      </div>

      <div className="mt-3 space-y-2 text-xs text-slate-700">
        {sorted.map((change, index) => {
          const previous = sorted[index - 1]
          const previousCreatedAt = previous?.createdAt

          return (
            <div key={change.id} className="rounded border border-slate-100 bg-slate-50 px-2 py-1.5">
              {/* Mirrors the status pill: colored dot + status-colored label */}
              <div className="inline-flex items-center gap-1.5 font-bold">
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${getServiceStatusMeta(change.newStatus).dotClass}`}
                />
                <span className={getServiceStatusMeta(change.newStatus).textClass}>
                  {formatServiceStatus(change.newStatus)}
                </span>
                <span className="font-normal text-slate-500">
                  (from {formatServiceStatus(change.previousStatus)})
                </span>
              </div>
              <div className="text-[11px] text-slate-500">
                {formatRelativeDateTime(change.createdAt)}
                {previousCreatedAt
                  ? ` · ${formatDurationBetween(previousCreatedAt, change.createdAt)} since previous`
                  : ''}
                {` · ${change.authorName}`}
              </div>
              {change.reason ? (
                <div className={`mt-1.5 ${noteCardClass}`}>
                  <NoteMarkdown
                    text={change.reason}
                    className="text-[12.5px] leading-normal text-stone-900"
                  />
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}
